/**
 * Floor API layer.
 *
 * DEVICE + PIN AUTH, NEVER ENTRA: every call carries the paired device token
 * in the `x-warehouselogix-device` header, and — once an operator has signed
 * in with a PIN — their operator token as `Authorization: Bearer`. The device
 * token is the perimeter; the operator token says who is working and is what
 * the API requires for WRITES. There is NO MSAL/Entra anywhere in this app.
 *
 * Tool calls go through the offline engine (floorNet): reads retry freely,
 * writes are journalled with a minted `_ik` and probe-settled after a drop.
 */

import type { ApiResult, FloorActionEnvelope, ToolId } from '@warehouselogix/shared';

import { API_BASE } from '../config';
import { DEVICE_HEADER, type DeviceContext } from '../device/deviceToken';

import { floorRead, floorWrite } from './floorNet';
import { floorAuthHeaders } from './transport';

/** Result of validating a pasted enrollment token against the API. */
export interface DeviceVerifyResult extends DeviceContext {
  deviceName: string;
}

/** Success payload of POST /floor/signin (mirrors the API's SignInResult). */
export interface SignInData {
  token: string;
  employeeId: string;
  displayName: string;
  /** ISO-8601 expiry. */
  expiresAt: string;
}

function apiBase(): string {
  return API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
}

/**
 * Validate a pasted enrollment token before persisting it (pairing screen).
 * Deliberately a bare fetch with the CANDIDATE token — the shared transport
 * would attach the stored one.
 */
export async function verifyDeviceToken(token: string): Promise<DeviceVerifyResult> {
  const response = await fetch(`${apiBase()}/floor/device/current`, {
    headers: { [DEVICE_HEADER]: token },
  });
  if (!response.ok) {
    throw new Error(`Device verify failed (${response.status})`);
  }
  const data = (await response.json()) as Partial<DeviceVerifyResult>;
  return {
    siteId: data.siteId ?? '',
    siteName: data.siteName ?? '',
    deviceName: data.deviceName ?? '',
  };
}

/**
 * PIN sign-in (`POST /floor/signin`). A bare fetch on purpose: it is not a
 * tool action (no idempotency, no journal) and must not go through the engine's
 * 401 handling. 200 carries an ApiResult either way; 429 is the device PIN
 * lockout; a 401 here means the DEVICE token is bad (the transport's rule).
 */
export async function signInWithPin(pin: string): Promise<ApiResult<SignInData>> {
  const response = await fetch(`${apiBase()}/floor/signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...floorAuthHeaders() },
    body: JSON.stringify({ pin }),
  });
  if (response.status === 429) {
    return {
      ok: false,
      error: {
        code: 'DEVICE_LOCKED',
        message: 'Too many attempts. Try again later.',
        retryable: true,
      },
    };
  }
  let body: unknown = null;
  try {
    body = (await response.json()) as unknown;
  } catch {
    body = null;
  }
  if (response.status === 200 && typeof body === 'object' && body !== null) {
    const r = body as ApiResult<SignInData>;
    if (r.ok === true && typeof r.data?.token === 'string') {
      return r;
    }
    if (r.ok === false && typeof r.error?.message === 'string') {
      return r;
    }
  }
  return {
    ok: false,
    error: {
      code: 'SIGNIN_FAILED',
      message: `Sign-in failed (${response.status}). Try again.`,
      retryable: true,
    },
  };
}

/**
 * Dispatch one tool action (`POST /floor/tools/:toolId`). The tool declares
 * which of its actions are reads; everything else is a write and gets the full
 * idempotency treatment. Callers pass action + payload — `_ik` is the
 * engine's business, which is why this takes the pieces rather than a
 * pre-built {@link FloorActionEnvelope}.
 */
export function callTool<T>(
  toolId: ToolId,
  action: string,
  payload: unknown,
  kind: 'read' | 'write'
): Promise<ApiResult<T>> {
  return kind === 'read'
    ? floorRead<T>(toolId, action, payload)
    : floorWrite<T>(toolId, action, payload);
}

/** Re-exported so tool screens import one module for their whole API surface. */
export type { ApiResult, FloorActionEnvelope };
